// campaign-message-bubble.tsx — the ONE message-preview bubble every Drip
// Campaigns surface renders (Templates list, Campaigns list, the wizard step
// cards and the sequence detail all share it). It wraps the app-wide
// `PreviewMessageBubble` — the SAME WhatsApp bubble the composer, team-inbox and
// scheduled messages use — so a template previews with real WhatsApp formatting
// (bold/italic/quotes), link/poll/attachment previews and `dir="auto"` text,
// identically everywhere. Sent-message convention → right-aligned by default.
//
// It replaces the prototype-ported `MessagePreview` primitive, which chipped
// `{tokens}` but rendered `*bold*` as literal asterisks and lacked real
// link/poll fidelity — the two surfaces disagreeing is exactly what this unifies.

import React from 'react';
import PreviewMessageBubble from '../add-scheduled-message-button/preview-message-bubble';
import {
  UserMessage,
  MessageAssetSchema,
  PollSchema,
  LinkPreviewSchema,
} from '@bt-common/schemas/bt/user-message';

// The template fields the preview needs. Both the bt-common Template (drip steps)
// and the campaign-model Template (standalone campaigns) provide these, so the
// helper accepts either. `poll`/`asset`/`linkPreview` are variant/untyped across
// the two Template shapes, so validate each through its schema — a match yields
// the typed value, a miss yields undefined (the bubble simply omits that part).
export interface PreviewableTemplate {
  message?: string | null;
  asset?: unknown;
  isPoll?: boolean;
  poll?: unknown;
  linkPreview?: unknown;
}

// Adapt a campaign/drip `Template` into the `Partial<UserMessage>` the bubble
// (and its link/attachment/poll child previews) consume — no casts. Purely
// presentational.
export function templateToPreviewMessage(tpl?: PreviewableTemplate | null): Partial<UserMessage> {
  const asset = MessageAssetSchema.safeParse(tpl?.asset);
  const poll = PollSchema.safeParse(tpl?.poll);
  const linkPreview = LinkPreviewSchema.safeParse(tpl?.linkPreview);
  return {
    message: tpl?.message ?? undefined,
    isPoll: tpl?.isPoll ?? undefined,
    asset: asset.success ? asset.data : undefined,
    poll: poll.success ? poll.data : undefined,
    linkPreview: linkPreview.success ? linkPreview.data : undefined,
  };
}

// A message with no renderable content — a green bubble would render empty, so
// callers show a muted note instead. (Distinct from a still-loading template.)
export function isEmptyPreview(pm: Partial<UserMessage>): boolean {
  return !pm.message?.trim() && !pm.asset && !pm.poll && !pm.linkPreview;
}

export interface CampaignMessageBubbleProps {
  /** The message to preview (build it from a template via `templateToPreviewMessage`). */
  message: Partial<UserMessage>;
  /** Bubble alignment inside its row. Sent-message default is 'end' (right). */
  align?: 'start' | 'center' | 'end';
  /** Bubble width cap, forwarded to the underlying preview bubble (default 85%
   *  chat-column feel; tight gallery cards pass '100%'). */
  maxWidth?: string | number;
  /** Stretch the bubble to fill the row (gallery cards) instead of chat-like
   *  fit-content sizing. Forwarded to the underlying preview bubble. */
  fullWidth?: boolean;
}

// The shared preview bubble: right-aligned (sent-message convention) real
// WhatsApp bubble, with the ack icon suppressed (a template/preview has no
// delivery state).
export const CampaignMessageBubble: React.FC<CampaignMessageBubbleProps> = ({ message, align = 'end', maxWidth, fullWidth }) => (
  <div style={{ display: 'flex', width: '100%', justifyContent: align === 'end' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start' }}>
    <PreviewMessageBubble messageOverride={message} shouldDisplayAckIcon={false} maxWidth={maxWidth} fullWidth={fullWidth} />
  </div>
);
